import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getOrganizationSubscriptions } from '../../../utils/api_service/admin';
import {
  DEFAULT_ORGANIZATION_VIEW_MODE,
  ORGANIZATION_PAGE_LIMIT,
  ORGANIZATION_SEARCH_DEBOUNCE_MS,
} from '../constants';
import type {
  IOrganizationSubscription,
  IOrganizationSubscriptionListResponse,
  IOrganizationTableRow,
  OrganizationFilters,
  OrganizationSortKey,
  OrganizationViewMode,
} from '../interfaces';

export interface IUseOrganizationsPage {
  viewMode: OrganizationViewMode;
  setViewMode: (mode: OrganizationViewMode) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  pageNumber: number;
  pageLimit: number;
  sortKey: OrganizationSortKey;
  setSortKey: (key: OrganizationSortKey) => void;
  activeFilters: OrganizationFilters;
  handleFiltersChange: (filters: OrganizationFilters) => void;
  organizationSubscriptions: IOrganizationSubscriptionListResponse;
  organizationRows: IOrganizationTableRow[];
  handleOrgClick: (org: IOrganizationSubscription) => void;
  handleRowClick: (row: IOrganizationTableRow) => void;
  handleCreate: () => void;
  handleEdit: (row: IOrganizationTableRow) => void;
  handleDelete: (row: IOrganizationTableRow) => void;
  handlePrevPage: () => void;
  handleNextPage: () => void;
}

const EMPTY_RESPONSE: IOrganizationSubscriptionListResponse = {
  data: [],
  page: 1,
  page_size: ORGANIZATION_PAGE_LIMIT,
  total: 0,
  total_pages: 0,
  totalOrganizations: 0,
  activeUsers: 0,
};

export const useOrganizationsPage = (): IUseOrganizationsPage => {
  const [viewMode, setViewMode] = useState<OrganizationViewMode>(DEFAULT_ORGANIZATION_VIEW_MODE);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageLimit] = useState<number>(ORGANIZATION_PAGE_LIMIT);
  const [sortKey, setSortKey] = useState<OrganizationSortKey>('latest');
  const [activeFilters, setActiveFilters] = useState<OrganizationFilters>({});
  const [organizationSubscriptions, setOrganizationSubscriptions] =
    useState<IOrganizationSubscriptionListResponse>(EMPTY_RESPONSE);

  const history = useHistory();

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      if (pageNumber !== 1) {
        setPageNumber(1);
      } else {
        void getOrganizations();
      }
    }, ORGANIZATION_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchQuery, pageLimit, pageNumber]);

  const getOrganizations = async (): Promise<void> => {
    try {
      const res = await getOrganizationSubscriptions(searchQuery, pageNumber, pageLimit);
      if (res.ok) {
        const response: IOrganizationSubscriptionListResponse = await res.json();
        setOrganizationSubscriptions(response);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const organizationRows: IOrganizationTableRow[] = organizationSubscriptions.data.map((item) => ({
    id: item.organization.id,
    source: item,
  }));

  const handleOrgClick = (org: IOrganizationSubscription): void => {
    history.push(`/organizations/${org.organization.id}/details`);
  };

  const handleRowClick = (row: IOrganizationTableRow): void => {
    handleOrgClick(row.source);
  };

  const handleCreate = (): void => {
    history.push('/organizations/create');
  };

  const handleEdit = (): void => {};

  const handleDelete = (): void => {};

  const handlePrevPage = (): void => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = (): void => {
    setPageNumber((prev) => prev + 1);
  };

  const handleFiltersChange = (filters: OrganizationFilters): void => {
    setActiveFilters(filters);
  };

  return {
    viewMode,
    setViewMode,
    searchQuery,
    setSearchQuery,
    pageNumber,
    pageLimit,
    sortKey,
    setSortKey,
    activeFilters,
    handleFiltersChange,
    organizationSubscriptions,
    organizationRows,
    handleOrgClick,
    handleRowClick,
    handleCreate,
    handleEdit,
    handleDelete,
    handlePrevPage,
    handleNextPage,
  };
};
